// TodayScheduleWidget — compact dashboard card showing every event
// happening TODAY across the workspace (tasks + deal deadlines +
// build milestones + manual events). Pulls from /api/v3/calendar/events.
//
// Displayed on V3Dashboard above the KPI grid. Empty state = quiet card
// with a link to open the full Calendar.

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { format, startOfDay, endOfDay, parseISO } from "date-fns";
import { CalendarDays, ArrowRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const LAYER_STYLE = {
  task:      { color: "#0EA5E9", label: "Task"    },
  deal:      { color: "#10B981", label: "Deal"    },
  build:     { color: "#F59E0B", label: "Build"   },
  closing:   { color: "#059669", label: "Closing" },
  jarvis:    { color: "#A855F7", label: "Jarvis"  },
  personal:  { color: "#64748B", label: "Event"   },
  marketing: { color: "#EF4444", label: "Marketing" },
};

function eventTimeLabel(e) {
  if (e.all_day) return "All day";
  try {
    return format(parseISO(e.starts_at), "h:mm a");
  } catch {
    return "";
  }
}

export default function TodayScheduleWidget() {
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const now = new Date();
        const res = await api.get("/v3/calendar/events", {
          params: {
            start:  startOfDay(now).toISOString(),
            end:    endOfDay(now).toISOString(),
            layers: "task,build,deal,closing,jarvis,personal,marketing",
          },
        });
        if (!cancelled) setEvents(res.data?.events || []);
      } catch {
        if (!cancelled) setEvents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const todayLabel = useMemo(
    () => format(new Date(), "EEEE, MMMM d"),
    []
  );

  return (
    <section
      data-testid="dashboard-today-schedule"
      className="rounded-2xl bg-white border border-slate-200/70 p-5 shadow-sm"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-lg grid place-items-center text-white shadow-sm bg-gradient-to-br from-amber-400 to-amber-600">
            <CalendarDays className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
              Today's schedule
            </div>
            <div className="text-[15px] font-bold text-slate-900 leading-tight">
              {todayLabel}
            </div>
          </div>
        </div>
        <Link
          to="/v3/calendar"
          data-testid="dashboard-today-schedule-open-calendar"
          className="text-[11.5px] font-semibold text-amber-600 hover:text-amber-700 inline-flex items-center gap-0.5"
        >
          Open Calendar <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      <div className="mt-4">
        {loading ? (
          <div className="flex items-center gap-2 text-[12px] text-slate-500 py-6 justify-center">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading today's events…
          </div>
        ) : events.length === 0 ? (
          <div
            data-testid="dashboard-today-schedule-empty"
            className="rounded-xl border border-dashed border-slate-200 bg-slate-50/40 p-4 text-center"
          >
            <div className="text-[13px] text-slate-700 font-semibold">
              Nothing on the calendar today
            </div>
            <div className="mt-1 text-[11.5px] text-slate-500">
              A clear runway. Use it to move a deal forward.
            </div>
            <Link
              to="/v3/calendar"
              className="mt-3 inline-flex items-center gap-1 text-[11.5px] font-semibold text-amber-600 hover:text-amber-700"
            >
              Add an event <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        ) : (
          <ul className="space-y-1.5" data-testid="dashboard-today-schedule-list">
            {events.slice(0, 6).map((e) => {
              const style = LAYER_STYLE[e.layer] || LAYER_STYLE.personal;
              return (
                <li key={e.id}>
                  <Link
                    to={e.property_id ? `/v3/properties/${e.property_id}` : "/v3/calendar"}
                    data-testid={`dashboard-today-event-${e.id}`}
                    className="flex items-start gap-2.5 rounded-lg border border-slate-200 hover:border-amber-300 hover:bg-amber-50/30 p-2.5 transition-colors"
                  >
                    <span
                      aria-hidden="true"
                      className="mt-1 h-2.5 w-2.5 rounded-full ring-2 ring-white shrink-0"
                      style={{ backgroundColor: e.color || style.color }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-slate-900 leading-snug truncate">
                        {e.title}
                      </div>
                      <div className="mt-0.5 flex items-center gap-1.5 flex-wrap text-[10.5px] text-slate-500">
                        <span className="font-medium">{eventTimeLabel(e)}</span>
                        <span
                          className="inline-block text-[9.5px] font-black uppercase tracking-widest rounded-full px-1.5 py-0.5"
                          style={{
                            backgroundColor: `${e.color || style.color}15`,
                            color: e.color || style.color,
                          }}
                        >
                          {style.label}
                        </span>
                      </div>
                    </div>
                  </Link>
                </li>
              );
            })}
            {events.length > 6 && (
              <li className="text-center pt-1">
                <Link
                  to="/v3/calendar"
                  className="text-[11.5px] font-semibold text-amber-600 hover:text-amber-700 inline-flex items-center gap-0.5"
                >
                  +{events.length - 6} more <ArrowRight className="w-3 h-3" />
                </Link>
              </li>
            )}
          </ul>
        )}
      </div>
    </section>
  );
}
